// internal deps
import { IObservable, Observable } from "thrive-observable-react";
import { CAS } from "./ChatConfig.Service";

// interfaces
import { IPeopleSearch, IPeopleSearchResults, IPeopleSearchUser, IUser } from "../interfaces";

//api
import { getPeopleSearchResults } from "../api";

//utilities
import { buildQueryString } from "../utility/queryStrings";



class PeopleSearch {

    private searchAbortController: AbortController | null = null;

    private paginationAbortController: AbortController | null = null;

    public searchInput: IObservable<string> = Observable<string>("");
    
    public searchResults: IObservable<IUser[]> = Observable<IUser[]>([]);
    
    public totalUserResults: IObservable<number> = Observable<number>(0);

    public peopleSearchPaginationUri: IObservable<string> = Observable<string>("");

    public resultsCountString: IObservable<string> = Observable<string>("");
    
    public isLoading: IObservable<boolean> = Observable<boolean>(false);

    public isLoadingMore: IObservable<boolean> = Observable<boolean>(false);

    public debounce: number = 0

    public init() {
        this.getPeopleSearch();
        this.totalUserResults.subscribe(() => this.setResultsCountString());
        this.searchInput.subscribe(() => {
            clearInterval(this.debounce)
            this.debounce = setTimeout(() => {
                this.getPeopleSearch();
            }, 1000);
            
        })
    }

    private getResultsMessageString(count: number, searchTerm: string): string {
        if (searchTerm) {
            return this.getSearchResultsMessageWithTerm(count, searchTerm);
        }
        return CAS.localisationStrings.SearchPeopleCount(count);
    }

    private getSearchResultsMessageWithTerm(count: number, searchTerm: string): string {
        if (count > 0) {
            return CAS.localisationStrings.SearchPeopleResults(count, searchTerm);
        }
        return CAS.localisationStrings.NoSearchPeopleResults(searchTerm);
    }
    
    private getUri(payload: IPeopleSearch) {
        return `api/user/search?${buildQueryString(payload)}`
    }

    public getSearchInputString() {
        return this.searchInput;
    }

    public getResultsMessage() {
        return this.resultsCountString;
    }

    public getIsLoading() {
        return this.isLoading;
    }

    public getIsLoadingMore() {
        return this.isLoadingMore;
    }

    public getPeopleSearch() {
        if (this.searchAbortController) {
            this.searchAbortController.abort();
        }
        this.setIsLoading(true);

        this.searchAbortController = new AbortController();
        const signal = this.searchAbortController.signal;
        
        const payload : IPeopleSearch = {
            nameFilter: this.searchInput.value,
            top:20,
            skip:0,
        }

        getPeopleSearchResults(this.getUri(payload), signal)
           .then(({ data }) => {
                this.handleSearchResponse(data, true);
            })
            .catch((error) => {
                console.error(`People Search Paginateion error`, error);
            })
            .finally(() => {
                this.setIsLoading(false);
                this.searchAbortController = null;
            }
        );
    }

    public getMorePeopleSearch() {
        if (this.isLoadingMore.value || this.paginationAbortController) return;
        this.setIsLoadingMore(true);

        this.paginationAbortController = new AbortController();
        const signal = this.paginationAbortController.signal;
        
        getPeopleSearchResults(this.peopleSearchPaginationUri.value, signal)
            .then(({data}) => {
                this.handleSearchResponse(data, false)
            })
           .catch((error) => {
                console.error(`People Search Paginateion error`, error);
            })
            .finally(() => {
                this.setIsLoadingMore(false);
                this.paginationAbortController = null;
            }
        );
    }

    private setTotalUserResults(total: number) {
        this.totalUserResults.update(total);
    }

    private setNewSearchResults(userProfiles: IPeopleSearchUser[]) {
        this.setSearchResults(this.mapPeopleSearchUsersToUsers(userProfiles))
    }

    private setPaginatedSearchResults(userProfiles: IPeopleSearchUser[]) {
        const currentPeopleSearchResults = this.searchResults.value;
        const newPeopleSearchResults = this.mapPeopleSearchUsersToUsers(userProfiles);
        this.setSearchResults([...currentPeopleSearchResults, ...newPeopleSearchResults])
    }

    private setSearchResults(userProfiles: IUser[]){
        this.searchResults.update(userProfiles);
    }

    private setIsLoading(isLoading: boolean){
        this.isLoading.update(isLoading);
    }

    private setIsLoadingMore(isLoading: boolean){
        this.isLoadingMore.update(isLoading);
    }

    private setPeopleSearchPaginationUri(uri: string | null) {
        this.peopleSearchPaginationUri.update(uri);
    }

    private setResultsCountString() {
        const searchTerm = this.searchInput.value.trim();
        const count = this.totalUserResults.value;
        const message = this.getResultsMessageString(count, searchTerm);
        
        this.resultsCountString.update(message);
    }

    public setSearchInput(input: string) {
        this.searchInput.update(input);
    }

    public setClearSearchInput() {
        this.searchInput.update("");
    }

    private mapPeopleSearchUsersToUsers(users: IPeopleSearchUser[]): IUser[] {
        return users.map(user => ({
            id: user.id,
            displayName: user.displayName,
            profileEmail: user.profileEmail,
            profilePhoneNumber: user.profilePhoneNumber,
            department: user.department,
            isActive: user.isActive,
            jobRole: user.jobRole,
            profilePicture: {
                id: user?.profilePicture?.id,
                fileName: user?.profilePicture?.filename,
                uri: user?.profilePicture?.uri,
                width: user?.profilePicture?.width,
                height: user?.profilePicture?.height
            }
        }));
    }

    private handleSearchResponse(data: IPeopleSearchResults, isNewSearch: boolean) {
        this.setTotalUserResults(data.totalProfileResults);
        this.setPeopleSearchPaginationUri(data.nextUrl);

        if (isNewSearch) {
            this.setNewSearchResults(data.profiles);
        } else {
            this.setPaginatedSearchResults(data.profiles);
        }
    }

    public handleClickContact(contact: IUser) {
        PS.setClearSearchInput();
        CAS.checkForExistingChat(contact);
    }

    public cleanUp(){
        this.searchInput.update("");
    
        this.searchResults.update([]);
    
        this.totalUserResults.update(0);

        this.peopleSearchPaginationUri.update("");

        this.resultsCountString.update("");
    
        this.isLoading.update(false);

        this.isLoadingMore.update(false);
    }
}

export const PS = new PeopleSearch();
